import { useQueryClient } from '@tanstack/react-query';
import { useEffect, useState } from 'react';

import { useApiClient } from '../api/apiContext';
import { queryKeys } from '../api/hooks';
import { readConfig } from '../config/env';
import { connectStomp, socketUrlFrom } from './stomp';

/**
 * Keeps a branch's day-board current (FR-401, ≤ 3 s).
 *
 * The server sends the branch and its new schedule version — never the board itself. A broadcast
 * carrying patient names would put them on a channel everyone in the clinic is subscribed to, and
 * would have to be rendered per recipient's language besides. Being told "your copy is stale" costs
 * one conditional refetch and keeps the fan-out payload free of anything worth protecting.
 *
 * The returned flag is for the reader, not for the code: a receptionist needs to know when the
 * board has stopped updating itself, because the alternative is trusting a screen that quietly
 * froze twenty minutes ago.
 */
export function useBoardStream(clinicId: string | null, branchId: string | null): boolean {
  const client = useApiClient();
  const queryClient = useQueryClient();
  const [live, setLive] = useState(false);

  useEffect(() => {
    if (!clinicId || !branchId) {
      setLive(false);
      return undefined;
    }

    const destination = `/topic/clinic/${clinicId}/branch/${branchId}/board`;
    const disconnect = connectStomp({
      url: socketUrlFrom(readConfig().apiBaseUrl),
      accessToken: () => client.getTokens()?.accessToken ?? null,
      onStateChange: setLive,
      subscriptions: [
        {
          destination,
          onMessage: () => {
            // Every day currently in the cache for this branch, not just the one on screen: a
            // booking made for tomorrow while somebody is looking at today still has to invalidate
            // tomorrow, or they will see stale data the moment they change the date.
            void queryClient.invalidateQueries({
              queryKey: ['clinics', clinicId, 'appointments', 'board', branchId],
            });
            void queryClient.invalidateQueries({
              queryKey: queryKeys.worklists(clinicId, branchId),
            });
          },
        },
      ],
    });

    return () => {
      disconnect();
      setLive(false);
    };
  }, [client, queryClient, clinicId, branchId]);

  return live;
}
